import { formatDistanceToNow } from 'date-fns';
import { SparklesIcon } from '@heroicons/react/24/outline';
import type { WorkflowStep } from '../../services/api/workflows.js';
import { StatusBadge } from './StatusBadge.js';
import { Button } from '../common/Button.js';
import { useConfirmManualStep } from '../../hooks/useWorkflows.js';

/** Config for manual-confirmation steps — keyed by step name. */
const MANUAL_STEP_CONFIG: Record<string, { awaitingLabel: string; buttonText: string }> = {
  'claude-removal': {
    awaitingLabel: 'Awaiting manual confirmation',
    buttonText: 'Confirm removed from Claude',
  },
  'claude-seat-approval': {
    awaitingLabel: 'Awaiting seat-capacity approval',
    buttonText: 'Approve Claude seats',
  },
  // MKS-463 — Slack Pro has no deactivation API, so an admin deactivates the member at
  // topmonks.slack.com and confirms here to release the paused offboarding run.
  'deactivate-slack': {
    awaitingLabel: 'Deactivate in topmonks.slack.com, then confirm',
    buttonText: 'Confirm deactivated in Slack',
  },
};

function formatRelativeTime(iso: string): string {
  return formatDistanceToNow(new Date(iso), { addSuffix: true });
}

function StepIcon({ name }: { name: string }) {
  const cls = 'h-4 w-4 flex-shrink-0';
  if (name.includes('google-workspace') || name.includes('delete-google'))
    return (
      <svg className={cls} viewBox="0 0 24 24">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.997 10.997 0 0 0 12 23Z" fill="#34A853"/>
        <path d="M5.84 14.09A6.6 6.6 0 0 1 5.5 12c0-.72.12-1.42.35-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.77.43 3.45 1.18 4.93l3.66-2.84Z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A10.94 10.94 0 0 0 12 1 11 11 0 0 0 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53Z" fill="#EA4335"/>
      </svg>
    );
  if (name.includes('email') || name.includes('invite-email'))
    return (
      <svg className={cls} viewBox="0 0 24 24">
        <path d="M24 5.457v11.09c0 .904-.732 1.636-1.636 1.636h-.819V8.18l-9.545 6-9.545-6v10.003H.818A1.636 1.636 0 0 1-.818 16.547V5.457c0-.497.228-.943.583-1.236A1.635 1.635 0 0 1 1.636 3.82L12 10.636 22.364 3.82a1.636 1.636 0 0 1 1.636 1.637Z" fill="#EA4335" transform="translate(0 1)"/>
      </svg>
    );
  if (name.includes('drive') || name.includes('transfer'))
    return (
      <svg className={cls} viewBox="0 0 87.3 78">
        <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3L29 52.4H0c0 1.55.4 3.1 1.2 4.5Z" fill="#0066DA"/>
        <path d="m43.65 25-15.25 26.4h30.5L74.15 25Z" fill="#00AC47"/>
        <path d="M58.9 52.4H29L13.75 76.8c1.35.8 2.9 1.2 4.5 1.2h31.3c1.6 0 3.15-.45 4.5-1.2Z" fill="#00832D"/>
        <path d="m29 52.4 15.25-26.4L29 0c-1.35.8-2.5 1.9-3.3 3.3L1.2 47.85c-.8 1.4-1.2 2.95-1.2 4.55Z" fill="#2684FC"/>
        <path d="M58.9 52.4 74.15 25c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25 29 52.4Z" fill="#FFBA00"/>
        <path d="M87.3 47.85 74.15 25c-.8-1.4-1.95-2.5-3.3-3.3l-27.2 30.7H87.3c0-1.6-.4-3.15-1.2-4.55Z" fill="#EA4335"/>
      </svg>
    );
  if (name.includes('slack'))
    return (
      <svg className={cls} viewBox="0 0 127 127" fill="none">
        <path d="M27.2 80a13.6 13.6 0 1 1-13.6-13.6h13.6V80Zm6.8 0a13.6 13.6 0 1 1 27.2 0v34a13.6 13.6 0 1 1-27.2 0V80Z" fill="#E01E5A"/>
        <path d="M47.6 27.2a13.6 13.6 0 1 1 13.6-13.6v13.6H47.6Zm0 6.8a13.6 13.6 0 1 1 0 27.2H13.6a13.6 13.6 0 1 1 0-27.2h34Z" fill="#36C5F0"/>
        <path d="M100.3 47.6a13.6 13.6 0 1 1 13.6 13.6h-13.6V47.6Zm-6.8 0a13.6 13.6 0 1 1-27.2 0v-34a13.6 13.6 0 1 1 27.2 0v34Z" fill="#2EB67D"/>
        <path d="M79.9 100.3a13.6 13.6 0 1 1-13.6 13.6v-13.6h13.6Zm0-6.8a13.6 13.6 0 1 1 0-27.2h34a13.6 13.6 0 1 1 0 27.2h-34Z" fill="#ECB22E"/>
      </svg>
    );
  if (name.includes('aws'))
    return (
      <svg className={cls} viewBox="0 0 64 64">
        <path d="M18.3 28.7c0 .9.1 1.6.3 2.1.2.5.5 1.1.9 1.7.1.2.2.4.2.5 0 .2-.1.4-.4.6l-1.2.8c-.2.1-.3.2-.5.2-.2 0-.4-.1-.5-.3-.3-.3-.5-.6-.7-.9-.2-.3-.4-.7-.6-1.1-1.5 1.8-3.4 2.7-5.7 2.7-1.6 0-2.9-.5-3.9-1.4s-1.5-2.2-1.5-3.7c0-1.6.6-3 1.7-3.9 1.2-1 2.7-1.5 4.7-1.5.7 0 1.3.1 2 .2s1.4.3 2.2.5v-1.4c0-1.5-.3-2.5-.9-3.1-.6-.6-1.7-.9-3.2-.9-.7 0-1.4.1-2.1.3s-1.4.4-2.1.7c-.3.1-.5.2-.7.3-.2 0-.3.1-.4.1-.3 0-.5-.2-.5-.7v-.9c0-.4.1-.6.2-.8.1-.1.3-.3.7-.4.7-.4 1.5-.7 2.4-.9 1-.3 2-.4 3.1-.4 2.3 0 4 .5 5.1 1.6 1 1.1 1.6 2.7 1.6 4.9v6.4Z" fill="#252F3E"/>
        <path d="M36.2 36.3c-.2.2-.5.2-1 .2-.5-.1-.8-.4-.8-.8 0-.7.1-1.2.1-1.6l-7.3-17.4c-.2-.5 0-.8.5-.8h1.7c.4 0 .7.1.8.3l5.2 13.7 4.8-13.7c.1-.2.4-.3.8-.3h1.6c.5 0 .7.3.5.8l-6.9 18.6Z" fill="#252F3E"/>
        <path d="M51.2 34.5c-1 0-1.8-.1-2.8-.4-.9-.3-1.6-.6-2.1-.9-.3-.2-.5-.4-.6-.6-.1-.2-.1-.4-.1-.5v-1c0-.5.2-.7.5-.7.1 0 .3 0 .4.1.1.1.3.2.5.3.7.3 1.4.6 2.2.8.8.2 1.5.3 2.3.3 1.2 0 2.1-.2 2.8-.7.6-.5 1-.9 1-1.9 0-.5-.2-1-.5-1.4-.4-.4-1-.7-1.9-1l-2.7-.9c-1.3-.4-2.3-1-3-1.9-.7-.8-1-1.7-1-2.7 0-.8.2-1.5.5-2.1.3-.6.8-1.1 1.3-1.5.6-.4 1.2-.7 1.9-.9.7-.2 1.5-.3 2.3-.3.4 0 .8 0 1.3.1.4.1.8.2 1.2.3.4.1.7.3 1 .4.3.1.6.3.8.4.3.2.5.3.6.5.1.2.2.4.2.7v.9c0 .5-.2.7-.5.7-.2 0-.5-.1-.8-.3-1.1-.5-2.4-.8-3.8-.8-1.1 0-1.9.2-2.5.6-.6.4-.9 1-.9 1.8 0 .6.2 1 .6 1.4.4.4 1.1.7 2.1 1l2.6.8c1.3.4 2.2 1 2.8 1.7.6.7.9 1.6.9 2.6 0 .8-.2 1.5-.5 2.2-.3.6-.8 1.2-1.4 1.6-.6.4-1.3.8-2.1 1-.9.2-1.7.4-2.7.4Z" fill="#252F3E"/>
        <path d="M57.4 44.8c-6.2 4.6-15.2 7-22.9 7-10.8 0-20.6-4-28-10.7-.6-.5-.1-1.2.6-.8 8 4.6 17.8 7.4 28 7.4 6.9 0 14.4-1.4 21.3-4.4 1-.5 1.9.7 1 1.5Z" fill="#FF9900"/>
        <path d="M59.7 42.2c-.8-1-5.2-.5-7.2-.2-.6.1-.7-.4-.2-.8 3.5-2.5 9.3-1.8 10-.9.7.8-.2 6.6-3.5 9.4-.5.4-1 .2-.8-.3.7-1.9 2.4-6.2 1.7-7.2Z" fill="#FF9900"/>
      </svg>
    );
  if (name.includes('github'))
    return (
      <svg className={cls} viewBox="0 0 98 96">
        <path fillRule="evenodd" clipRule="evenodd" d="M48.854 0C21.839 0 0 22 0 49.217c0 21.756 13.993 40.172 33.405 46.69 2.427.49 3.316-1.059 3.316-2.362 0-1.141-.08-5.052-.08-9.127-13.59 2.934-16.42-5.867-16.42-5.867-2.184-5.704-5.42-7.17-5.42-7.17-4.448-3.015.324-3.015.324-3.015 4.934.326 7.523 5.052 7.523 5.052 4.367 7.496 11.404 5.378 14.235 4.074.404-3.178 1.699-5.378 3.074-6.6-10.839-1.141-22.243-5.378-22.243-24.283 0-5.378 1.94-9.778 5.014-13.2-.485-1.222-2.184-6.275.486-13.038 0 0 4.125-1.304 13.426 5.052a46.97 46.97 0 0 1 12.214-1.63c4.125 0 8.33.571 12.213 1.63 9.302-6.356 13.427-5.052 13.427-5.052 2.67 6.763.97 11.816.485 13.038 3.155 3.422 5.015 7.822 5.015 13.2 0 18.905-11.404 23.06-22.324 24.283 1.78 1.548 3.316 4.481 3.316 9.126 0 6.6-.08 11.897-.08 13.526 0 1.304.89 2.853 3.316 2.364 19.412-6.52 33.405-24.935 33.405-46.691C97.707 22 75.788 0 48.854 0Z" fill="#24292f"/>
      </svg>
    );
  if (name.includes('claude'))
    return <SparklesIcon className={cls} aria-hidden="true" />;
  return null;
}

export function WorkflowStepList({ steps, userId }: { steps: WorkflowStep[]; userId: number }) {
  const confirmMutation = useConfirmManualStep(userId);

  return (
    <div className="pl-6 border-l-2 border-gray-100 mt-2 mb-3 space-y-2">
      {steps.map((step) => {
        const manualConfig = MANUAL_STEP_CONFIG[step.name];
        return (
          <div key={step.id} className="flex items-center gap-3 py-2 min-h-[44px] flex-wrap">
            <StatusBadge state={step.status} size="sm" />
            <StepIcon name={step.name} />
            <span className="text-sm text-gray-900">{step.name}</span>
            <time dateTime={step.updatedAt} className="text-xs text-gray-400">
              {formatRelativeTime(step.updatedAt)}
            </time>
            {step.status === 'failed' && step.errorMessage && (
              <span className="text-xs text-red-600">{step.errorMessage}</span>
            )}
            {step.attemptCount > 1 && (
              <span className="text-xs text-gray-400">
                Attempt {step.attemptCount},{' '}
                <time dateTime={step.updatedAt}>{formatRelativeTime(step.updatedAt)}</time>
              </span>
            )}
            {manualConfig && step.status === 'running' && (
              <>
                <span className="text-xs text-amber-700">{manualConfig.awaitingLabel}</span>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    confirmMutation.mutate(step.id);
                  }}
                  isLoading={confirmMutation.isPending && confirmMutation.variables === step.id}
                  disabled={confirmMutation.isPending}
                >
                  {manualConfig.buttonText}
                </Button>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
